import { useState } from 'react';
import { captureEvent } from '../lib/posthog';

export interface NotificationItem {
  id: string;
  label: string;
  detail: string;
  type: 'nudge' | 'reflection' | 'chat' | 'review_queue' | 'outbound_breaker';
}

interface NotificationCenterProps {
  items: NotificationItem[];
  onSelect: (item: NotificationItem) => void;
}

export function NotificationCenter({ items, onSelect }: NotificationCenterProps) {
  const [open, setOpen] = useState(false);
  const unread = items.length;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-xl border border-edge bg-white hover:bg-canvas transition-colors"
        aria-label="Notifications"
      >
        <span className="material-icons-round text-ink-secondary">notifications</span>
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-error text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-[90vw] bg-white border border-edge rounded-xl shadow-raised z-40 overflow-hidden">
          <div className="px-3 py-2 border-b border-edge bg-canvas">
            <p className="text-sm font-semibold text-ink" data-ph-no-mask>Proactive Updates</p>
          </div>
          {items.length === 0 ? (
            <p className="px-3 py-4 text-sm text-ink-tertiary" data-ph-no-mask>No unread updates</p>
          ) : (
            <div className="max-h-80 overflow-y-auto">
              {items.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    captureEvent('notification_clicked', { type: item.type });
                    onSelect(item);
                    setOpen(false);
                  }}
                  className="w-full text-left px-3 py-3 border-b last:border-b-0 border-edge/50 hover:bg-canvas transition-colors"
                >
                  <p className="text-sm font-medium text-ink">{item.label}</p>
                  <p className="text-xs text-ink-secondary mt-1">{item.detail}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default NotificationCenter;
